import { useMemo } from "react";
import { motion } from "framer-motion";
import { BarChart3, Clock, Users, Tag, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CauseDistribution {
  category: string;
  count: number;
  percentage: number;
}

interface FrictionPoint {
  id: string;
  location: string;
  hazard_category: string;
  primary_cause: string | null;
  report_count: number;
  friction_score: number;
  estimated_impact: 'high' | 'medium' | 'low' | null;
  peak_shift?: string | null;
  affected_teams?: string[];
  cause_distribution?: CauseDistribution[];
}

interface FrictionPatternAnalysisProps {
  points: FrictionPoint[];
}

const causeLabels: Record<string, string> = {
  'equipment': 'Peralatan',
  'time_pressure': 'Tekanan Waktu',
  'training': 'Pelatihan',
  'environment': 'Lingkungan',
  'ppe': 'APD',
  'procedure': 'Prosedur',
  'fatigue': 'Kelelahan',
  'communication': 'Komunikasi',
  'supervision': 'Pengawasan'
};

const shiftLabels: Record<string, string> = {
  'morning': 'Pagi',
  'afternoon': 'Siang',
  'night': 'Malam'
};

export const FrictionPatternAnalysis = ({ points }: FrictionPatternAnalysisProps) => {
  // Analyze by shift
  const shiftAnalysis = useMemo(() => {
    const shifts: Record<string, { count: number; totalScore: number; highImpact: number }> = {
      morning: { count: 0, totalScore: 0, highImpact: 0 },
      afternoon: { count: 0, totalScore: 0, highImpact: 0 },
      night: { count: 0, totalScore: 0, highImpact: 0 }
    };

    points.forEach(p => {
      const shift = p.peak_shift || 'morning';
      if (shifts[shift]) {
        shifts[shift].count += p.report_count;
        shifts[shift].totalScore += p.friction_score;
        if (p.estimated_impact === 'high') shifts[shift].highImpact++;
      }
    });

    return Object.entries(shifts)
      .map(([shift, data]) => ({
        shift,
        label: shiftLabels[shift] || shift,
        ...data,
        avgScore: data.count > 0 ? data.totalScore / Object.values(shifts).filter(s => s.count > 0).length : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [points]);

  // Analyze by category
  const categoryAnalysis = useMemo(() => {
    const cats: Record<string, { count: number; reports: number; highImpact: number }> = {};

    points.forEach(p => {
      const cat = p.hazard_category;
      if (!cats[cat]) {
        cats[cat] = { count: 0, reports: 0, highImpact: 0 };
      }
      cats[cat].count++;
      cats[cat].reports += p.report_count;
      if (p.estimated_impact === 'high') cats[cat].highImpact++;
    });

    return Object.entries(cats)
      .map(([category, data]) => ({
        category,
        label: category.replace(/_/g, ' '),
        ...data
      }))
      .sort((a, b) => b.reports - a.reports)
      .slice(0, 6);
  }, [points]);

  // Analyze by cause
  const causeAnalysis = useMemo(() => {
    const causes: Record<string, number> = {};

    points.forEach(p => {
      if (p.cause_distribution) {
        p.cause_distribution.forEach(c => {
          causes[c.category] = (causes[c.category] || 0) + c.count;
        });
      } else if (p.primary_cause) {
        // Try to extract cause category from primary_cause text
        const causeLower = p.primary_cause.toLowerCase();
        if (causeLower.includes('equipment') || causeLower.includes('tool')) {
          causes['equipment'] = (causes['equipment'] || 0) + 1;
        } else if (causeLower.includes('time') || causeLower.includes('pressure')) {
          causes['time_pressure'] = (causes['time_pressure'] || 0) + 1;
        } else if (causeLower.includes('training')) {
          causes['training'] = (causes['training'] || 0) + 1;
        }
      }
    });

    const total = Object.values(causes).reduce((a, b) => a + b, 0);
    
    return Object.entries(causes)
      .map(([cause, count]) => ({
        cause,
        label: causeLabels[cause] || cause,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [points]);

  // Analyze by team
  const teamAnalysis = useMemo(() => {
    const teams: Record<string, number> = {};

    points.forEach(p => {
      if (p.affected_teams) {
        p.affected_teams.forEach(team => {
          teams[team] = (teams[team] || 0) + p.report_count;
        });
      }
    });

    return Object.entries(teams)
      .map(([team, count]) => ({ team, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [points]);

  const maxCategoryReports = Math.max(...categoryAnalysis.map(c => c.reports), 1);
  const maxShiftCount = Math.max(...shiftAnalysis.map(s => s.count), 1);
  const maxCauseCount = Math.max(...causeAnalysis.map(c => c.count), 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* By Shift */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Berdasarkan Shift
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {shiftAnalysis.map((shift, idx) => (
            <motion.div
              key={shift.shift}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="space-y-1"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{shift.label}</span>
                <div className="flex items-center gap-2">
                  {shift.highImpact > 0 && (
                    <Badge variant="destructive" className="text-xs h-5">
                      {shift.highImpact} kritis
                    </Badge>
                  )}
                  <span className="text-muted-foreground">{shift.count} laporan</span>
                </div>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(shift.count / maxShiftCount) * 100}%` }}
                  transition={{ delay: idx * 0.05 + 0.2 }}
                  className={cn(
                    "h-full rounded-full",
                    shift.shift === 'night' ? 'bg-indigo-500' :
                    shift.shift === 'afternoon' ? 'bg-amber-500' :
                    'bg-sky-500'
                  )}
                />
              </div>
            </motion.div>
          ))}
        </CardContent>
      </Card>

      {/* By Category */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            Berdasarkan Kategori
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {categoryAnalysis.map((cat, idx) => (
            <motion.div
              key={cat.category}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="space-y-1"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium capitalize">{cat.label}</span>
                <div className="flex items-center gap-2">
                  {cat.highImpact > 0 && (
                    <span className="text-red-500 text-xs">🔴 {cat.highImpact}</span>
                  )}
                  <span className="text-muted-foreground">{cat.reports}</span>
                </div>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(cat.reports / maxCategoryReports) * 100}%` }}
                  transition={{ delay: idx * 0.05 + 0.2 }}
                  className="h-full rounded-full bg-primary"
                />
              </div>
            </motion.div>
          ))}
        </CardContent>
      </Card>

      {/* By Cause */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Penyebab Utama
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {causeAnalysis.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Data penyebab belum tersedia
            </p>
          ) : (
            causeAnalysis.map((cause, idx) => (
              <motion.div
                key={cause.cause}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="w-16 text-right">
                  <span className="text-lg font-bold">{cause.percentage}%</span>
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(cause.count / maxCauseCount) * 100}%` }}
                      transition={{ delay: idx * 0.05 + 0.2 }}
                      className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{cause.label}</span>
                </div>
              </motion.div>
            ))
          )}
        </CardContent>
      </Card>

      {/* By Team */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Tim Terdampak
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teamAnalysis.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Data tim belum tersedia
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {teamAnalysis.map((team, idx) => (
                <motion.div
                  key={team.team}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-sm py-1.5",
                      idx === 0 && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                  >
                    {team.team}
                    <span className="ml-1 opacity-60">({team.count})</span>
                  </Badge>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FrictionPatternAnalysis;
